import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  User,
  Buildings,
  ArrowLeft,
  SignOut,
  SpinnerGap,
  Eye,
  X,
  Phone,
  EnvelopeSimple,
  FileText,
  LinkedinLogo,
  DownloadSimple,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { ADMIN_TOKEN_KEY } from './AdminLoginPage';
import {
  PROFILE_STATUSES,
  REQUEST_STATUSES,
  STATUS_LABEL_AR,
} from '../constants/talentBank';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLOR = {
  New: { bg: '#1F3D2B1A', text: '#1F3D2B' },
  Reviewed: { bg: '#3D512D1A', text: '#3D512D' },
  Suitable: { bg: '#4A641F1A', text: '#4A641F' },
  'Not Suitable': { bg: '#FEE2E2', text: '#B91C1C' },
  Archived: { bg: '#E5E7EB', text: '#4B5563' },
  'Under Review': { bg: '#FEF3C7', text: '#B45309' },
  Contacted: { bg: '#DBEAFE', text: '#1E40AF' },
  Closed: { bg: '#E5E7EB', text: '#4B5563' },
};

const AdminTalentBankPage = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('profiles');
  const [profiles, setProfiles] = useState([]);
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // { type, item }

  const auth = () => {
    const token = localStorage.getItem(ADMIN_TOKEN_KEY);
    if (!token) navigate('/admin/login');
    return token;
  };

  const fetchAll = useCallback(async () => {
    const token = auth();
    if (!token) return;
    setLoading(true);
    try {
      const [pr, rq, st] = await Promise.all([
        fetch(`${API_URL}/api/professional-profiles`),
        fetch(`${API_URL}/api/talent-requests`),
        fetch(`${API_URL}/api/talent-bank/stats`),
      ]);
      setProfiles(await pr.json());
      setRequests(await rq.json());
      setStats(await st.json());
    } catch (e) {
      toast.error('فشل تحميل البيانات');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const updateStatus = async (type, id, status) => {
    const path =
      type === 'profile' ? `professional-profiles/${id}/status` : `talent-requests/${id}/status`;
    try {
      const res = await fetch(`${API_URL}/api/${path}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error('فشل التحديث');
      toast.success('تم تحديث الحالة');
      fetchAll();
      setSelected(null);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const logout = () => {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    navigate('/admin/login');
  };

  return (
    <main
      data-testid="admin-talent-bank-page"
      className="pt-24 pb-16 min-h-screen bg-[#FAFAF7]"
      dir="rtl"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#1F3D2B] font-['IBM_Plex_Sans_Arabic']">
              لوحة بنك الكفاءات
            </h1>
            <p className="text-sm text-[#6B7565] mt-1">
              إدارة الملفات المهنية وطلبات الكفاءات
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/admin/requests')}
              className="text-sm text-[#1F3D2B] border border-[#D8D2C8] hover:bg-[#F5F2EC] rounded-xl px-4 py-2 transition-colors"
            >
              ← طلبات الاستشارات
            </button>
            <button
              onClick={logout}
              className="text-sm text-[#1F3D2B] border border-[#D8D2C8] hover:bg-[#F5F2EC] rounded-xl px-4 py-2 flex items-center gap-2 transition-colors"
              data-testid="admin-logout-btn"
            >
              <SignOut size={16} />
              خروج
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          {[
            { label: 'إجمالي الملفات', value: stats.total_profiles ?? 0, icon: User },
            { label: 'ملفات جديدة', value: stats.new_profiles ?? 0, icon: User },
            { label: 'إجمالي الطلبات', value: stats.total_requests ?? 0, icon: Buildings },
            { label: 'طلبات جديدة', value: stats.new_requests ?? 0, icon: Buildings },
          ].map((s) => {
            const Icon = s.icon;
            return (
              <div
                key={s.label}
                className="bg-white rounded-2xl border border-[#E6E0D6] p-4 sm:p-5"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-[#6B7565] mb-1">{s.label}</p>
                    <p className="text-2xl font-bold text-[#1F3D2B]">{s.value}</p>
                  </div>
                  <div className="w-10 h-10 rounded-xl bg-[#F5F2EC] flex items-center justify-center">
                    <Icon size={20} weight="duotone" className="text-[#4A641F]" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-4">
          <button
            data-testid="tab-profiles"
            onClick={() => setTab('profiles')}
            className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-colors ${
              tab === 'profiles'
                ? 'bg-[#1F3D2B] text-white'
                : 'bg-white border border-[#E6E0D6] text-[#3D512D] hover:bg-[#F5F2EC]'
            }`}
          >
            الملفات المهنية ({profiles.length})
          </button>
          <button
            data-testid="tab-requests"
            onClick={() => setTab('requests')}
            className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-colors ${
              tab === 'requests'
                ? 'bg-[#1F3D2B] text-white'
                : 'bg-white border border-[#E6E0D6] text-[#3D512D] hover:bg-[#F5F2EC]'
            }`}
          >
            طلبات الجهات ({requests.length})
          </button>
        </div>

        {/* List */}
        {loading ? (
          <div className="text-center py-20 text-[#6B7565]">
            <SpinnerGap size={32} className="animate-spin mx-auto mb-3 text-[#4A641F]" />
            جاري التحميل…
          </div>
        ) : tab === 'profiles' ? (
          <div className="bg-white rounded-2xl border border-[#E6E0D6] overflow-hidden">
            {profiles.length === 0 ? (
              <div className="text-center py-12 text-[#6B7565]">لا توجد ملفات بعد.</div>
            ) : (
              <ul className="divide-y divide-[#E6E0D6]">
                {profiles.map((p) => {
                  const c = STATUS_COLOR[p.status] || STATUS_COLOR.New;
                  return (
                    <li
                      key={p.id}
                      className="p-4 sm:p-5 flex flex-wrap items-center justify-between gap-3 hover:bg-[#FAFAF7] transition-colors"
                      data-testid={`profile-row-${p.id}`}
                    >
                      <div className="flex items-start gap-3 min-w-0 flex-1">
                        <div className="w-10 h-10 rounded-xl bg-[#F5F2EC] flex items-center justify-center shrink-0">
                          <User size={20} weight="duotone" className="text-[#4A641F]" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-bold text-[#1F3D2B] truncate">{p.full_name}</p>
                          <p className="text-xs text-[#6B7565] truncate mt-0.5">
                            {p.specialization} · {p.city} · خبرة: {p.years_of_experience}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span
                          className="px-2.5 py-1 rounded-full text-xs font-medium"
                          style={{ background: c.bg, color: c.text }}
                        >
                          {STATUS_LABEL_AR[p.status] || p.status}
                        </span>
                        <button
                          onClick={() => setSelected({ type: 'profile', item: p })}
                          data-testid={`view-profile-${p.id}`}
                          className="text-xs text-[#1F3D2B] border border-[#D8D2C8] hover:bg-[#F5F2EC] rounded-lg px-3 py-1.5 flex items-center gap-1"
                        >
                          <Eye size={14} />
                          عرض
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-[#E6E0D6] overflow-hidden">
            {requests.length === 0 ? (
              <div className="text-center py-12 text-[#6B7565]">لا توجد طلبات بعد.</div>
            ) : (
              <ul className="divide-y divide-[#E6E0D6]">
                {requests.map((r) => {
                  const c = STATUS_COLOR[r.status] || STATUS_COLOR.New;
                  return (
                    <li
                      key={r.id}
                      className="p-4 sm:p-5 flex flex-wrap items-center justify-between gap-3 hover:bg-[#FAFAF7] transition-colors"
                      data-testid={`request-row-${r.id}`}
                    >
                      <div className="flex items-start gap-3 min-w-0 flex-1">
                        <div className="w-10 h-10 rounded-xl bg-[#F5F2EC] flex items-center justify-center shrink-0">
                          <Buildings size={20} weight="duotone" className="text-[#4A641F]" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-bold text-[#1F3D2B] truncate">
                            {r.entity_name}
                          </p>
                          <p className="text-xs text-[#6B7565] truncate mt-0.5">
                            {r.entity_type} · {r.city} · تخصص: {r.required_specialization}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span
                          className="px-2.5 py-1 rounded-full text-xs font-medium"
                          style={{ background: c.bg, color: c.text }}
                        >
                          {STATUS_LABEL_AR[r.status] || r.status}
                        </span>
                        <button
                          onClick={() => setSelected({ type: 'request', item: r })}
                          data-testid={`view-request-${r.id}`}
                          className="text-xs text-[#1F3D2B] border border-[#D8D2C8] hover:bg-[#F5F2EC] rounded-lg px-3 py-1.5 flex items-center gap-1"
                        >
                          <Eye size={14} />
                          عرض
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </div>

      {/* Detail modal */}
      {selected && (
        <div
          className="fixed inset-0 bg-black/55 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto"
          onClick={() => setSelected(null)}
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-[24px] max-w-2xl w-full p-6 sm:p-8 my-8 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            dir="rtl"
          >
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-xl font-bold text-[#1F3D2B] font-['IBM_Plex_Sans_Arabic']">
                {selected.type === 'profile' ? 'تفاصيل الملف المهني' : 'تفاصيل طلب الجهة'}
              </h3>
              <button
                onClick={() => setSelected(null)}
                className="text-[#7A866E] hover:text-[#1F3D2B]"
              >
                <X size={22} />
              </button>
            </div>

            {selected.type === 'profile' ? (
              <ProfileDetail item={selected.item} />
            ) : (
              <RequestDetail item={selected.item} />
            )}

            {/* Status actions */}
            <div className="mt-6 pt-5 border-t border-[#E6E0D6]">
              <p className="text-sm text-[#1F3D2B] font-bold mb-3">تحديث الحالة:</p>
              <div className="flex flex-wrap gap-2">
                {(selected.type === 'profile' ? PROFILE_STATUSES : REQUEST_STATUSES).map((s) => (
                  <button
                    key={s}
                    onClick={() => updateStatus(selected.type, selected.item.id, s)}
                    className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${
                      selected.item.status === s
                        ? 'bg-[#1F3D2B] text-white border-[#1F3D2B]'
                        : 'border-[#D8D2C8] text-[#3D512D] hover:bg-[#F5F2EC]'
                    }`}
                  >
                    {STATUS_LABEL_AR[s] || s}
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </main>
  );
};

const Row = ({ label, value, dir }) =>
  value ? (
    <div className="py-2 grid grid-cols-[140px_1fr] gap-3 text-sm border-b border-[#F1ECE3]">
      <span className="text-[#6B7565]">{label}</span>
      <span className="text-[#1F3D2B] break-words" dir={dir}>
        {value}
      </span>
    </div>
  ) : null;

const ProfileDetail = ({ item }) => (
  <div>
    <Row label="الاسم" value={item.full_name} />
    <Row label="المدينة" value={item.city} />
    <Row label="الجنسية" value={item.nationality} />
    <Row label="البريد" value={item.email} dir="ltr" />
    <Row label="الجوال" value={item.mobile} dir="ltr" />
    <Row label="المؤهل" value={item.academic_qualification} />
    <Row label="التخصص" value={item.specialization} />
    <Row label="سنوات الخبرة" value={item.years_of_experience} />
    <Row label="المنصب" value={item.current_or_previous_role} />
    <Row label="المهارات" value={item.professional_skills} />
    <Row label="الدورات والشهادات" value={item.courses_and_certifications} />
    <Row label="مجالات الاهتمام" value={item.preferred_areas_of_interest} />
    <Row label="موقع العمل المفضل" value={item.preferred_city_or_work_location} />
    <Row label="طبيعة العمل المرغوبة" value={
      item.preferred_work_mode === 'remote' ? 'عن بُعد (أونلاين)'
      : item.preferred_work_mode === 'onsite' ? 'ميداني (حضوري)'
      : item.preferred_work_mode === 'hybrid' ? 'هجين (جزئي)'
      : item.preferred_work_mode === 'flexible' ? 'مرن (حسب الاتفاق)'
      : ''
    } />
    <Row label="حاصل على رخصة مستشار" value={
      item.has_consultant_license === true ? 'نعم'
      : item.has_consultant_license === false ? 'لا'
      : ''
    } />
    {item.has_consultant_license === true && (
      <Row label="جهة الترخيص" value={item.licensing_authority} />
    )}
    <Row label="نبذة" value={item.short_professional_bio} />
    {item.cv_file_url && (
      <div className="py-2 border-b border-[#F1ECE3]">
        <a
          href={`${API_URL}${item.cv_file_url}`}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-2 text-sm text-[#4A641F] hover:underline"
        >
          <DownloadSimple size={16} />
          تحميل السيرة الذاتية (PDF)
        </a>
      </div>
    )}
    {item.linkedin_profile_url && (
      <div className="py-2 border-b border-[#F1ECE3]">
        <a
          href={item.linkedin_profile_url}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-2 text-sm text-[#0077B5] hover:underline"
        >
          <LinkedinLogo size={16} />
          LinkedIn
        </a>
      </div>
    )}
  </div>
);

const RequestDetail = ({ item }) => (
  <div>
    <Row label="اسم الجهة" value={item.entity_name} />
    <Row label="نوع الجهة" value={item.entity_type} />
    <Row label="المدينة" value={item.city} />
    <Row label="مسؤول التواصل" value={item.contact_person_name} />
    <Row label="المسمى الوظيفي" value={item.contact_person_job_title} />
    <Row label="البريد" value={item.email} dir="ltr" />
    <Row label="الجوال" value={item.mobile} dir="ltr" />
    <Row label="التخصص المطلوب" value={item.required_specialization} />
    <Row label="المؤهل المطلوب" value={item.required_academic_qualification} />
    <Row label="الخبرة المفضلة" value={item.preferred_years_of_experience} />
    <Row label="طريقة التواصل" value={item.preferred_communication_method} />
    <Row label="وصف الاحتياج" value={item.professional_need_description} />
    <Row label="ملاحظات" value={item.additional_notes} />
  </div>
);

export default AdminTalentBankPage;
